import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import ChatMarkdown from "./ChatMarkdown";
import {
  KaiLiveSession,
  type LiveStatus,
  type LiveTurn,
} from "../lib/kaiLiveSession";
import "../styles/animations.css";

type ChatRole = "user" | "bot" | "error";

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
  /** Present on error messages — the user text to resend. */
  retryText?: string;
  /** Epoch ms before which Retry stays locked (rate-limit errors). */
  retryAt?: number;
};

type QueuedQuestion = {
  id: string;
  text: string;
};

const MAX_QUEUE = 3;
/** Rate-limit pauses up to this long are waited out automatically. */
const AUTO_RETRY_MAX_SEC = 90;
const AUTO_RETRY_ATTEMPTS = 10;
/** Backoff before each automatic retry of a transient (5xx / network) failure. */
const transientBackoffMs = (attempt: number) =>
  Math.min(1000 * 2 ** attempt, 8000);
const isTransientStatus = (status: number) => status >= 500 && status < 600;

/** Playful status lines shown while Walt keeps working on a slow/transient turn. */
const RETRY_DESCRIPTORS_EARLY = [
  "checking...",
  "digging into his personal things...",
  "discovering..",
  "looking into it...",
  "musing...",
  "poking around...",
  "piecing it together...",
  "having a look...",
  "snooping ...",
  "getting nosy...",
];
/** Once things drag on (7th try onward), lean into the apologetic register. */
const RETRY_DESCRIPTORS_LATE = [
  "double-checking...",
  "confirming...",
  "it's taking a bit long...",
  "sorry, hold on...",
  "brb...",
  "hmmm....",
  "errr......",
  "one more moment...",
  "one more time...",
  "bear with me...",
  "not giving up...",
  "wrangling something...",
  "this one's stubborn...",
  "my bad, still on it...",
  "wow, this is a tough one...",
];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Pick a status line for the given 1-based try, avoiding an immediate repeat.
 * Tries 1–6 draw from the early pool; the 7th onward from the "hold on" pool.
 */
const pickRetryDescriptor = (attempt: number, previous?: string | null) => {
  const pool = attempt <= 6 ? RETRY_DESCRIPTORS_EARLY : RETRY_DESCRIPTORS_LATE;
  const choices =
    pool.length > 1 ? pool.filter((d) => d !== previous) : pool;
  return choices[Math.floor(Math.random() * choices.length)];
};

const formatCountdown = (sec: number) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = String(sec % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

class ChatError extends Error {
  readonly retryAfter?: number;

  constructor(message: string, retryAfter?: number) {
    super(message);
    this.retryAfter = retryAfter;
  }
}

const PRESET_QUESTIONS = [
  "Who is Walt?",
  "Show me a photo of Walt's cat.",
] as const;

/**
 * Presets answered entirely client-side — no Gemini call. Walt "thinks" for one
 * cycle, then renders the canned Markdown reply (image included).
 */
const CANNED_REPLIES: Record<string, string> = {
  "Show me a photo of Walt's cat.":
    'Here\'s Artemis — my cat, whom I nickname "Artis":\n\n![Artemis, Walt\'s cat](/assets/walt-cat-artemis.jpg)',
};
/** How long Walt "types" before a canned reply lands. */
const CANNED_REPLY_DELAY_MS = 700;

type AshParticle = {
  x: number;
  y: number;
  size: number;
  color: string;
  ember: boolean;
  dx: number;
  dy: number;
  rot: number;
  delay: number;
  dur: number;
};

const ASH_COLORS = ["#9ca3af", "#6b7280", "#d1d5db", "#4b5563", "#9ca3af"];
const EMBER_COLORS = ["#fb923c", "#f59e0b"];
/** Sweep (700ms) + max particle delay/duration, after which the chip is dropped. */
const ASH_TOTAL_MS = 1900;

function makeAshParticles(width: number, height: number): AshParticle[] {
  const count = Math.min(80, Math.max(24, Math.round((width * height) / 45)));
  return Array.from({ length: count }, () => {
    const x = Math.random() * width;
    const ember = Math.random() < 0.08;
    return {
      x,
      y: Math.random() * height,
      size: 1.5 + Math.random() * 2.5,
      color: ember
        ? EMBER_COLORS[Math.floor(Math.random() * EMBER_COLORS.length)]
        : ASH_COLORS[Math.floor(Math.random() * ASH_COLORS.length)],
      ember,
      dx: 8 + Math.random() * 36,
      dy: 30 + Math.random() * 50,
      rot: (Math.random() - 0.5) * 360,
      // Follow the left-to-right sweep of the chip's mask.
      delay: (x / width) * 600 + Math.random() * 100,
      dur: 700 + Math.random() * 450,
    };
  });
}

/** How long the remaining chips take to slide into the freed-up space. */
const CHIP_SLIDE_MS = 350;

const isPreset = (text: string) =>
  (PRESET_QUESTIONS as readonly string[]).includes(text);

export default function InlineChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [queue, setQueue] = useState<QueuedQuestion[]>([]);
  const [isSending, setIsSending] = useState(false);
  /** Seconds left on an automatic rate-limit retry, shown in the typing line. */
  const [retryIn, setRetryIn] = useState<number | null>(null);
  /** Playful status line shown while an automatic retry is in progress. */
  const [retrying, setRetrying] = useState<string | null>(null);
  /** Ticks once a second while an error's Retry is locked. */
  const [now, setNow] = useState(() => Date.now());
  const listRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);
  const queueRef = useRef<QueuedQuestion[]>([]);
  /** Preset questions that were answered: gone or mid-disintegration. */
  const [usedPresets, setUsedPresets] = useState<Set<string>>(new Set());
  const [ashing, setAshing] = useState<Map<string, AshParticle[]>>(new Map());
  /** Preset questions queued or awaiting a reply; their chips stay put. */
  const [pendingPresets, setPendingPresets] = useState<Set<string>>(new Set());
  /** An unresolved error pauses the chat until it is retried or dismissed. */
  const errorPendingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const chipsRef = useRef<HTMLDivElement>(null);
  /** Chip positions captured just before an ashed chip leaves the layout. */
  const chipRectsRef = useRef<Map<string, DOMRect> | null>(null);

  const [speechOn, setSpeechOn] = useState(false);
  const [speechStatus, setSpeechStatus] = useState<LiveStatus | null>(null);
  const [speechTranscript, setSpeechTranscript] = useState("");
  const [speechError, setSpeechError] = useState<string | null>(null);
  const liveSessionRef = useRef<KaiLiveSession | null>(null);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Slide the remaining chips from their old spots into the reflowed layout.
  useLayoutEffect(() => {
    const before = chipRectsRef.current;
    const container = chipsRef.current;
    if (!before || !container) return;
    chipRectsRef.current = null;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    container.querySelectorAll<HTMLElement>("[data-chip]").forEach((el) => {
      const prev = before.get(el.dataset.chip!);
      if (!prev) return;
      const next = el.getBoundingClientRect();
      const dx = prev.left - next.left;
      const dy = prev.top - next.top;
      if (!dx && !dy) return;
      el.animate(
        [
          { transform: `translate(${dx}px, ${dy}px)` },
          { transform: "translate(0, 0)" },
        ],
        { duration: CHIP_SLIDE_MS, easing: "cubic-bezier(0.2, 0, 0, 1)" },
      );
    });
  }, [ashing]);

  const lockedUntil = Math.max(0, ...messages.map((m) => m.retryAt ?? 0));
  useEffect(() => {
    if (lockedUntil <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [lockedUntil]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isSending]);

  const commitSpeechTurn = (turn: LiveTurn) => {
    const next: ChatMessage[] = [];
    if (turn.userText) {
      next.push({
        id: `u-speech-${Date.now()}`,
        role: "user",
        text: turn.userText,
      });
    }
    if (turn.kaiText) {
      next.push({
        id: `b-speech-${Date.now() + 1}`,
        role: "bot",
        text: turn.kaiText,
      });
    }
    if (!next.length) return;
    setMessages((prev) => [...prev, ...next]);
  };

  const stopSpeechMode = () => {
    const session = liveSessionRef.current;
    liveSessionRef.current = null;
    session?.stop(); // commits any in-flight turn via onTurnComplete
    setSpeechOn(false);
    setSpeechStatus(null);
    setSpeechTranscript("");
  };

  const startSpeechMode = async () => {
    if (speechOn || liveSessionRef.current || errorPendingRef.current) return;
    setSpeechError(null);
    setSpeechTranscript("");
    setSpeechOn(true);
    setSpeechStatus("connecting");

    const session = new KaiLiveSession({
      onStatus: (status) => setSpeechStatus(status),
      onOutputTranscript: (text) => setSpeechTranscript(text),
      onTurnComplete: (turn) => commitSpeechTurn(turn),
      onError: (message) => {
        setSpeechError(message);
        setSpeechStatus("error");
      },
    });
    liveSessionRef.current = session;

    try {
      await session.start();
    } catch (err) {
      liveSessionRef.current = null;
      setSpeechOn(false);
      setSpeechStatus(null);
      setSpeechError(
        err instanceof Error ? err.message : "Could not start speech mode.",
      );
    }
  };

  const toggleSpeechMode = () => {
    if (speechOn) stopSpeechMode();
    else void startSpeechMode();
  };

  useEffect(() => {
    return () => {
      liveSessionRef.current?.stop({ commit: false });
      liveSessionRef.current = null;
    };
  }, []);

  const enqueueOrSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || errorPendingRef.current) return;

    if (busyRef.current || isSending) {
      if (queueRef.current.length >= MAX_QUEUE) return;
      const item: QueuedQuestion = { id: `q-${Date.now()}`, text: trimmed };
      const next = [...queueRef.current, item];
      queueRef.current = next;
      setQueue(next);
      return;
    }

    void runTurn(trimmed);
  };

  const clearPendingPreset = (text: string) => {
    setPendingPresets((prev) => {
      if (!prev.has(text)) return prev;
      const next = new Set(prev);
      next.delete(text);
      return next;
    });
  };

  /** Turn an answered preset chip to ash and drop it once the particles settle. */
  const ashOutPreset = (question: string) => {
    clearPendingPreset(question);
    const button = document.querySelector<HTMLButtonElement>(
      `[data-preset="${CSS.escape(question)}"]`,
    );
    if (!button) return;
    const { width, height } = button.getBoundingClientRect();
    setAshing((prev) =>
      new Map(prev).set(question, makeAshParticles(width, height)),
    );
    setUsedPresets((prev) => new Set(prev).add(question));

    window.setTimeout(() => {
      const rects = new Map<string, DOMRect>();
      chipsRef.current
        ?.querySelectorAll<HTMLElement>("[data-chip]")
        .forEach((el) => rects.set(el.dataset.chip!, el.getBoundingClientRect()));
      chipRectsRef.current = rects;
      setAshing((prev) => {
        const next = new Map(prev);
        next.delete(question);
        return next;
      });
    }, ASH_TOTAL_MS);
  };

  const handlePresetClick = (question: string) => {
    if (
      queueFull ||
      errorPendingRef.current ||
      usedPresets.has(question) ||
      pendingPresets.has(question)
    ) {
      return;
    }
    setPendingPresets((prev) => new Set(prev).add(question));
    enqueueOrSend(question);
  };

  const runTurn = async (
    text: string,
    opts?: { skipUserBubble?: boolean },
  ) => {
    busyRef.current = true;
    setIsSending(true);

    if (!opts?.skipUserBubble) {
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: "user", text },
      ]);
    }

    try {
      // Some presets never touch the model: wait one cycle, then answer locally.
      const canned = CANNED_REPLIES[text];
      if (canned) {
        await sleep(CANNED_REPLY_DELAY_MS);
        setMessages((prev) => [
          ...prev,
          { id: `b-${Date.now()}`, role: "bot", text: canned },
        ]);
        if (isPreset(text)) ashOutPreset(text);
        return;
      }

      let res: Response | undefined;
      let data: { reply?: string; error?: string; retryAfter?: number } = {};
      for (let attempt = 0; ; attempt++) {
        try {
          res = await fetch("/api/chat", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: text }),
          });
          data = await res.json().catch(() => ({}));
        } catch (networkErr) {
          // Offline / connection reset: treat like a transient server error.
          if (attempt >= AUTO_RETRY_ATTEMPTS) throw networkErr;
          res = undefined;
        }

        if (res?.status === 429) {
          const wait = data.retryAfter ?? 0;
          if (!wait || wait > AUTO_RETRY_MAX_SEC || attempt >= AUTO_RETRY_ATTEMPTS) {
            break;
          }
          for (let left = Math.ceil(wait); left > 0; left--) {
            setRetryIn(left);
            await sleep(1000);
          }
          setRetryIn(null);
          continue;
        }

        if (res && (!isTransientStatus(res.status) || attempt >= AUTO_RETRY_ATTEMPTS)) {
          break;
        }
        setRetrying((prev) => pickRetryDescriptor(attempt + 1, prev));
        await sleep(transientBackoffMs(attempt));
      }

      if (!res?.ok) {
        throw new ChatError(
          data.error || "Could not reach assistant.",
          res?.status === 429 ? data.retryAfter : undefined,
        );
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `b-${Date.now()}`,
          role: "bot",
          text: data.reply || "No reply returned.",
        },
      ]);
      if (isPreset(text)) ashOutPreset(text);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not reach assistant.";
      const retryAfter = err instanceof ChatError ? err.retryAfter : undefined;
      errorPendingRef.current = true;
      clearPendingPreset(text);
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: "error",
          text: message,
          retryText: text,
          retryAt: retryAfter ? Date.now() + retryAfter * 1000 : undefined,
        },
      ]);
      setNow(Date.now());
    } finally {
      setRetryIn(null);
      setRetrying(null);
      busyRef.current = false;
      setIsSending(false);
      drainQueue();
    }
  };

  /** Send the next queued question, unless an error is waiting on the user. */
  const drainQueue = () => {
    if (busyRef.current || errorPendingRef.current) return;
    const [next, ...rest] = queueRef.current;
    if (!next) return;
    setQueue(rest);
    queueRef.current = rest;
    void runTurn(next.text);
  };

  const handleRetry = (errorId: string, retryText: string) => {
    if (busyRef.current || isSending) return;
    const err = messages.find((m) => m.id === errorId);
    if (err?.retryAt && err.retryAt > Date.now()) return;
    errorPendingRef.current = false;
    setMessages((prev) => prev.filter((m) => m.id !== errorId));
    if (isPreset(retryText)) {
      setPendingPresets((prev) => new Set(prev).add(retryText));
    }
    void runTurn(retryText, { skipUserBubble: true });
  };

  const handleDismissError = (errorId: string) => {
    errorPendingRef.current = false;
    setMessages((prev) => prev.filter((m) => m.id !== errorId));
    drainQueue();
    if (!queueRef.current.length) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    enqueueOrSend(text);
  };

  const cancelQueued = (id: string) => {
    const item = queueRef.current.find((q) => q.id === id);
    if (item) clearPendingPreset(item.text);
    const next = queueRef.current.filter((item) => item.id !== id);
    queueRef.current = next;
    setQueue(next);
  };

  const downloadTranscript = () => {
    const lines = messages.map((m) => {
      const who = m.role === "user" ? "You" : m.role === "error" ? "Error" : "Walt";
      return `**${who}:** ${m.text}`;
    });
    const body = `# Chat with Walt\n\n_${new Date().toLocaleString()}_\n\n${lines.join("\n\n")}\n`;
    const url = URL.createObjectURL(new Blob([body], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `kai-transcript-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const queueFull = isSending && queue.length >= MAX_QUEUE;
  const errorPending = messages.some((m) => m.role === "error");
  const speechPhase =
    speechStatus === "speaking"
      ? "speaking"
      : speechStatus === "connecting"
        ? "connecting"
        : "listening";
  const speechStatusLabel =
    speechStatus === "connecting"
      ? "Connecting…"
      : speechStatus === "speaking"
        ? "Speaking…"
        : speechStatus === "error"
          ? "Speech error"
          : speechOn
            ? "Listening…"
            : null;

  return (
    <section
      id="inline-chat"
      aria-label="Chat with Walt Yao's AI assistant"
      className={`glass-panel relative w-full mb-10 p-4 md:p-5 ${
        speechOn ? "speech-active" : ""
      } ${speechStatus === "speaking" ? "is-speaking" : ""}`}
    >
      <header className="relative mb-3 px-10 text-center">
        <h2 className="text-lg md:text-xl font-semibold text-blue-200">
          Ask me almost anything, about Walt.
        </h2>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={downloadTranscript}
            title="Save transcript"
            aria-label="Save transcript"
            className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              aria-hidden="true"
            >
              <path
                d="M10 3v9m0 0-3.5-3.5M10 12l3.5-3.5M4 14.5V16h12v-1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </header>

      <div
        ref={chipsRef}
        className="mb-3 flex flex-wrap items-center gap-2"
        role="group"
        aria-label="Suggested questions"
      >
        {PRESET_QUESTIONS.map((question) => {
          const particles = ashing.get(question);
          if (usedPresets.has(question) && !particles) return null;
          return (
            <span
              key={question}
              data-chip={question}
              className="relative inline-flex"
            >
              <button
                type="button"
                data-preset={question}
                onClick={() => handlePresetClick(question)}
                disabled={
                  queueFull ||
                  errorPending ||
                  !!particles ||
                  pendingPresets.has(question)
                }
                aria-hidden={particles ? true : undefined}
                tabIndex={particles ? -1 : undefined}
                className={`rounded-lg border border-white/15 bg-black/25 px-3 py-1.5 text-left text-xs md:text-sm text-gray-200 hover:border-[#42D49C]/50 hover:bg-black/40 hover:text-white transition-colors disabled:opacity-50 ${
                  particles ? "ash-out disabled:opacity-100" : ""
                }`}
              >
                {question}
              </button>
              {particles?.map((p, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className="ash-particle"
                  style={
                    {
                      left: p.x,
                      top: p.y,
                      width: p.size,
                      height: p.size,
                      background: p.color,
                      boxShadow: p.ember ? `0 0 4px ${p.color}` : undefined,
                      "--dx": `${p.dx}px`,
                      "--dy": `${p.dy}px`,
                      "--rot": `${p.rot}deg`,
                      "--delay": `${p.delay}ms`,
                      "--dur": `${p.dur}ms`,
                    } as CSSProperties
                  }
                />
              ))}
            </span>
          );
        })}
      </div>

      <div className="relative mb-3 border-b border-white/10">
        <div
          ref={listRef}
          className="min-h-[9rem] max-h-[18rem] overflow-y-auto scrollbar-styled pb-3 space-y-3"
          role="log"
          aria-live="polite"
        >
        {messages.length === 0 && !isSending && !speechOn && (
          <p className="text-sm text-gray-500">
            Pick a question above, or type your own.
          </p>
        )}
        {messages.map((msg) => {
          const label =
            msg.role === "user" ? "You" : msg.role === "error" ? "Error" : "Walt";
          const color =
            msg.role === "user"
              ? "text-blue-200"
              : msg.role === "error"
                ? "text-red-300"
                : "text-emerald-200";

          if (msg.role === "error") {
            return (
              <div
                key={msg.id}
                role="alert"
                className="flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-sm leading-relaxed"
              >
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-300"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495a1.75 1.75 0 0 1 3.03 0l6.28 10.875A1.75 1.75 0 0 1 16.28 16H3.72a1.75 1.75 0 0 1-1.515-2.63l6.28-10.875ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="min-w-0 flex-1">
                  <strong className={color}>{label}</strong>
                  <p className="text-gray-200 whitespace-pre-wrap">{msg.text}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1 self-center">
                  {msg.retryText && (() => {
                    const wait = msg.retryAt
                      ? Math.ceil((msg.retryAt - now) / 1000)
                      : 0;
                    return (
                      <button
                        type="button"
                        onClick={() => handleRetry(msg.id, msg.retryText!)}
                        disabled={isSending || wait > 0}
                        aria-label={
                          wait > 0 ? `Retry available in ${wait} seconds` : "Retry"
                        }
                        className="btn-teal btn-sm tabular-nums disabled:opacity-50"
                      >
                        {wait > 0 ? `Retry in ${formatCountdown(wait)}` : "Retry"}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => handleDismissError(msg.id)}
                    className="p-1.5 rounded text-red-200/70 hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Dismiss error"
                    title="Dismiss"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 3l8 8M11 3L3 11" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="text-sm leading-relaxed">
              <strong className={color}>{label}:</strong>
              {msg.role === "bot" ? (
                <ChatMarkdown text={msg.text} />
              ) : (
                <>
                  {" "}
                  <span className="text-gray-200 whitespace-pre-wrap">
                    {msg.text}
                  </span>
                </>
              )}
            </div>
          );
        })}
        {isSending && !speechOn && (
          <div className="text-sm text-gray-400 animate-pulse">
            {retryIn
              ? `Walt is catching his breath — back in ${retryIn}s…`
              : retrying
                ? retrying
                : "Walt is typing…"}
          </div>
        )}
        {speechOn && speechTranscript && (
          <div className="text-sm leading-relaxed" aria-live="polite">
            <strong className="text-emerald-200">Walt:</strong>{" "}
            <span className="text-gray-200 whitespace-pre-wrap">
              {speechTranscript}
            </span>
          </div>
        )}
        </div>
      </div>

      {speechError && !speechOn && (
        <div
          role="alert"
          className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-2.5 py-1.5 text-sm text-red-200"
        >
          <span className="min-w-0 flex-1">{speechError}</span>
          <button
            type="button"
            onClick={() => setSpeechError(null)}
            className="shrink-0 p-1 rounded text-red-200/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Dismiss speech error"
          >
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M3 3l8 8M11 3L3 11" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {queue.length > 0 && !speechOn && (
        <ul className="mb-2 space-y-1.5" aria-label="Queued questions">
          {queue.map((item, index) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-lg bg-black/25 border border-white/10 px-2.5 py-1.5 text-sm"
            >
              <span className="text-gray-500 shrink-0 tabular-nums">
                {index + 1}.
              </span>
              <span className="flex-1 text-gray-300 truncate" title={item.text}>
                {item.text}
              </span>
              <button
                type="button"
                onClick={() => cancelQueued(item.id)}
                className="shrink-0 p-1 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                aria-label={`Remove queued question: ${item.text}`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M3 3l8 8M11 3L3 11" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <label htmlFor="user-input" className="sr-only">
          Ask a question
        </label>
        <div className="relative flex-1">
          <input
            id="user-input"
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              speechOn
                ? ""
                : errorPending
                  ? "Retry or dismiss the error to keep chatting…"
                  : queueFull
                    ? "Queue full (max 3) — wait for a reply…"
                    : isSending
                      ? "Type next question to queue…"
                      : "Ask a question…"
            }
            required={!speechOn}
            disabled={queueFull || errorPending || speechOn}
            maxLength={4000}
            autoComplete="off"
            className={`w-full rounded-lg bg-black/30 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#42D49C]/60 disabled:opacity-50 transition-[filter] duration-300 ${
              speechOn ? "blur-[2px] opacity-40" : ""
            }`}
          />
          {speechOn && (
            <div
              className="pointer-events-none absolute inset-0 z-10 flex items-center gap-2 px-3 text-sm text-fuchsia-100"
              aria-live="polite"
            >
              <span
                className="speech-wave speech-wave-sm text-fuchsia-300"
                data-phase={speechPhase}
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
                <span />
              </span>
              <span className="font-medium">{speechStatusLabel}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center">
          {input.trim() && !speechOn ? (
            <button
              type="submit"
              disabled={queueFull || errorPending}
              className="btn-teal btn-sm shrink-0 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              Send
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  d="M6 12 3.27 3.13A59.77 59.77 0 0 1 21.49 12 59.77 59.77 0 0 1 3.27 20.87L6 12Zm0 0h7.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={toggleSpeechMode}
              disabled={errorPending || (!speechOn && isSending)}
              aria-pressed={speechOn}
              title={speechOn ? "End Live speech mode" : "Start Live speech mode"}
              aria-label={speechOn ? "End Live speech mode" : "Start Live speech mode"}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs md:text-sm font-semibold text-white transition-all disabled:opacity-50 ${
                speechOn
                  ? "bg-gradient-to-r from-pink-500/90 to-indigo-500/90 shadow-[0_0_10px_rgba(255,47,208,0.4)]"
                  : "bg-gradient-to-r from-fuchsia-600/80 to-violet-600/80 hover:from-fuchsia-500 hover:to-violet-500 shadow-[0_0_8px_rgba(167,47,247,0.35)]"
              }`}
            >
              Live
              {speechOn ? (
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <rect x="5" y="5" width="10" height="10" rx="1.5" />
                </svg>
              ) : (
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  aria-hidden="true"
                >
                  <path
                    d="M10 3.5a2.5 2.5 0 0 0-2.5 2.5v4a2.5 2.5 0 1 0 5 0v-4A2.5 2.5 0 0 0 10 3.5Z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5.5 10a4.5 4.5 0 0 0 9 0M10 14.5V16.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
